'use client';
import { kInternal } from '@liveblocks/core';
import { useClient, useCreateThread, useCreateComment, useEditComment } from '@liveblocks/react';
import React__default, { forwardRef, useRef, useCallback } from 'react';
import { AttachmentIcon } from '../icons/Attachment.mjs';
import { EmojiIcon } from '../icons/Emoji.mjs';
import { MentionIcon } from '../icons/Mention.mjs';
import { SendIcon } from '../icons/Send.mjs';
import { useOverrides } from '../overrides.mjs';
import { Form as ComposerForm, Editor as ComposerEditor, Submit as ComposerSubmit, AttachFiles as ComposerAttachFiles, Mention as ComposerMention$1, Suggestions as ComposerSuggestions, SuggestionsList as ComposerSuggestionsList, SuggestionsListItem as ComposerSuggestionsListItem, Link as ComposerLink$1 } from '../primitives/Composer/index.mjs';
import { useComposer, useComposerAttachmentsContext } from '../primitives/Composer/contexts.mjs';
import { useComposerAttachmentsDropArea } from '../primitives/Composer/utils.mjs';
import { MENTION_CHARACTER } from '../slate/plugins/mentions.mjs';
import { classNames } from '../utils/class-names.mjs';
import { useControllableState } from '../utils/use-controllable-state.mjs';
import { useLayoutEffect } from '../utils/use-layout-effect.mjs';
import { FileAttachment } from './internal/Attachment.mjs';
import { Attribution } from './internal/Attribution.mjs';
import { Avatar } from './internal/Avatar.mjs';
import { Button } from './internal/Button.mjs';
import { EmojiPicker } from './internal/EmojiPicker.mjs';
import { ShortcutTooltip, ShortcutTooltipKey, Tooltip } from './internal/Tooltip.mjs';
import { User } from './internal/User.mjs';
import { TooltipProvider } from '@radix-ui/react-tooltip';
import { PopoverTrigger } from '@radix-ui/react-popover';

function ComposerInsertMentionEditorAction({
  label,
  tooltipLabel,
  className,
  onClick,
  ...props
}) {
  const { createMention } = useComposer();
  const preventDefault = useCallback((event) => {
    event.preventDefault();
  }, []);
  const handleClick = useCallback(
    (event) => {
      onClick?.(event);
      if (!event.isDefaultPrevented()) {
        event.stopPropagation();
        createMention();
      }
    },
    [createMention, onClick]
  );
  return /* @__PURE__ */ React__default.createElement(Tooltip, {
    content: tooltipLabel ?? label
  }, /* @__PURE__ */ React__default.createElement(Button, {
    className: classNames("lb-composer-editor-action", className),
    onMouseDown: preventDefault,
    onClick: handleClick,
    "aria-label": label,
    ...props
  }, /* @__PURE__ */ React__default.createElement(MentionIcon, {
    className: "lb-button-icon"
  })));
}
function ComposerInsertEmojiEditorAction({
  label,
  tooltipLabel,
  onPickerOpenChange,
  className,
  ...props
}) {
  const { insertText } = useComposer();
  const preventDefault = useCallback((event) => {
    event.preventDefault();
  }, []);
  const stopPropagation = useCallback((event) => {
    event.stopPropagation();
  }, []);
  return /* @__PURE__ */ React__default.createElement(EmojiPicker, {
    onEmojiSelect: insertText,
    onOpenChange: onPickerOpenChange
  }, /* @__PURE__ */ React__default.createElement(Tooltip, {
    content: tooltipLabel ?? label
  }, /* @__PURE__ */ React__default.createElement(PopoverTrigger, {
    asChild: true
  }, /* @__PURE__ */ React__default.createElement(Button, {
    className: classNames("lb-composer-editor-action", className),
    onMouseDown: preventDefault,
    onClick: stopPropagation,
    "aria-label": label,
    ...props
  }, /* @__PURE__ */ React__default.createElement(EmojiIcon, {
    className: "lb-button-icon"
  })))));
}
function ComposerAttachFilesEditorAction({
  label,
  tooltipLabel,
  className,
  ...props
}) {
  const preventDefault = useCallback((event) => {
    event.preventDefault();
  }, []);
  const stopPropagation = useCallback((event) => {
    event.stopPropagation();
  }, []);
  return /* @__PURE__ */ React__default.createElement(Tooltip, {
    content: tooltipLabel ?? label
  }, /* @__PURE__ */ React__default.createElement(ComposerAttachFiles, {
    asChild: true
  }, /* @__PURE__ */ React__default.createElement(Button, {
    className: classNames("lb-composer-editor-action", className),
    onMouseDown: preventDefault,
    onClick: stopPropagation,
    "aria-label": label,
    ...props
  }, /* @__PURE__ */ React__default.createElement(AttachmentIcon, {
    className: "lb-button-icon"
  }))));
}
function ComposerMention({ userId }) {
  return /* @__PURE__ */ React__default.createElement(ComposerMention$1, {
    className: "lb-composer-mention"
  }, MENTION_CHARACTER, /* @__PURE__ */ React__default.createElement(User, {
    userId
  }));
}
function ComposerMentionSuggestions({
  userIds
}) {
  return userIds.length > 0 ? /* @__PURE__ */ React__default.createElement(ComposerSuggestions, {
    className: "lb-root lb-portal lb-elevation lb-composer-suggestions lb-composer-mention-suggestions"
  }, /* @__PURE__ */ React__default.createElement(ComposerSuggestionsList, {
    className: "lb-composer-suggestions-list lb-composer-mention-suggestions-list"
  }, userIds.map((userId) => /* @__PURE__ */ React__default.createElement(ComposerSuggestionsListItem, {
    key: userId,
    className: "lb-composer-suggestions-list-item lb-composer-mention-suggestion",
    value: userId
  }, /* @__PURE__ */ React__default.createElement(Avatar, {
    userId,
    className: "lb-composer-mention-suggestion-avatar"
  }), /* @__PURE__ */ React__default.createElement(User, {
    userId,
    className: "lb-composer-mention-suggestion-user"
  }))))) : null;
}
function ComposerLink({ href, children }) {
  return /* @__PURE__ */ React__default.createElement(ComposerLink$1, {
    href,
    className: "lb-composer-link"
  }, children);
}
function ComposerFileAttachment({
  attachment,
  className,
  overrides,
  ...props
}) {
  const { removeAttachment } = useComposer();
  const handleDeleteClick = useCallback(() => {
    removeAttachment(attachment.id);
  }, [attachment.id, removeAttachment]);
  return /* @__PURE__ */ React__default.createElement(FileAttachment, {
    className: classNames("lb-composer-attachment", className),
    ...props,
    attachment,
    onDeleteClick: handleDeleteClick,
    preventFocusOnDelete: true,
    overrides
  });
}
function ComposerAttachments({
  overrides,
  className,
  ...props
}) {
  const { attachments } = useComposer();
  if (attachments.length === 0) {
    return null;
  }
  return /* @__PURE__ */ React__default.createElement("div", {
    className: classNames("lb-composer-attachments", className),
    ...props
  }, /* @__PURE__ */ React__default.createElement("div", {
    className: "lb-attachments"
  }, attachments.map((attachment) => {
    return /* @__PURE__ */ React__default.createElement(ComposerFileAttachment, {
      key: attachment.id,
      attachment,
      overrides
    });
  })));
}
const editorComponents = {
  Mention: ComposerMention,
  MentionSuggestions: ComposerMentionSuggestions,
  Link: ComposerLink
};
function ComposerEditorContainer({
  showAttachments = true,
  showAttribution,
  defaultValue,
  isCollapsed,
  overrides,
  actions,
  autoFocus,
  disabled,
  hasResolveMentionSuggestions,
  onEmojiPickerOpenChange,
  onEmptyChange,
  onEditorClick
}) {
  const { isEmpty } = useComposer();
  const { hasMaxAttachments } = useComposerAttachmentsContext();
  const $ = useOverrides(overrides);
  const [isDraggingOver, dropAreaProps] = useComposerAttachmentsDropArea({
    disabled: disabled || hasMaxAttachments
  });
  useLayoutEffect(() => {
    onEmptyChange(isEmpty);
  }, [isEmpty, onEmptyChange]);
  const preventDefault = useCallback((event) => {
    event.preventDefault();
  }, []);
  const stopPropagation = useCallback((event) => {
    event.stopPropagation();
  }, []);
  return /* @__PURE__ */ React__default.createElement("div", {
    className: "lb-composer-editor-container",
    ...dropAreaProps
  }, /* @__PURE__ */ React__default.createElement(ComposerEditor, {
    className: "lb-composer-editor",
    onClick: onEditorClick,
    placeholder: $.COMPOSER_PLACEHOLDER,
    defaultValue,
    autoFocus,
    components: editorComponents,
    disabled,
    dir: $.dir
  }), showAttachments && /* @__PURE__ */ React__default.createElement(ComposerAttachments, {
    overrides
  }), (!isCollapsed || isDraggingOver) && /* @__PURE__ */ React__default.createElement("div", {
    className: "lb-composer-footer"
  }, /* @__PURE__ */ React__default.createElement("div", {
    className: "lb-composer-editor-actions"
  }, hasResolveMentionSuggestions && /* @__PURE__ */ React__default.createElement(ComposerInsertMentionEditorAction, {
    label: $.COMPOSER_INSERT_MENTION,
    disabled
  }), /* @__PURE__ */ React__default.createElement(ComposerInsertEmojiEditorAction, {
    label: $.COMPOSER_INSERT_EMOJI,
    onPickerOpenChange: onEmojiPickerOpenChange,
    disabled
  }), showAttachments && /* @__PURE__ */ React__default.createElement(ComposerAttachFilesEditorAction, {
    label: $.COMPOSER_ATTACH_FILES,
    disabled
  })), showAttribution && /* @__PURE__ */ React__default.createElement(Attribution, null), /* @__PURE__ */ React__default.createElement("div", {
    className: "lb-composer-actions"
  }, actions ?? /* @__PURE__ */ React__default.createElement(React__default.Fragment, null, /* @__PURE__ */ React__default.createElement(ShortcutTooltip, {
    content: $.COMPOSER_SEND,
    shortcut: /* @__PURE__ */ React__default.createElement(ShortcutTooltipKey, {
      name: "enter"
    })
  }, /* @__PURE__ */ React__default.createElement(ComposerSubmit, {
    asChild: true
  }, /* @__PURE__ */ React__default.createElement(Button, {
    onMouseDown: preventDefault,
    onClick: stopPropagation,
    className: "lb-composer-action",
    variant: "primary",
    "aria-label": $.COMPOSER_SEND
  }, /* @__PURE__ */ React__default.createElement(SendIcon, null))))))), showAttachments && isDraggingOver && /* @__PURE__ */ React__default.createElement("div", {
    className: "lb-composer-attachments-drop-area"
  }, /* @__PURE__ */ React__default.createElement("div", {
    className: "lb-composer-attachments-drop-area-label"
  }, /* @__PURE__ */ React__default.createElement(AttachmentIcon, null), $.COMPOSER_ATTACH_FILES)));
}
const Composer = forwardRef(
  ({
    threadId,
    commentId,
    metadata,
    defaultValue,
    defaultAttachments,
    onComposerSubmit,
    collapsed: controlledCollapsed,
    defaultCollapsed,
    onCollapsedChange: controlledOnCollapsedChange,
    overrides,
    actions,
    onBlur,
    className,
    onFocus,
    autoFocus,
    disabled,
    showAttachments = true,
    showAttribution,
    ...props
  }, forwardedRef) => {
    const client = useClient();
    const createThread = useCreateThread();
    const createComment = useCreateComment();
    const editComment = useEditComment();
    const hasResolveMentionSuggestions = client[kInternal].resolveMentionSuggestions !== void 0;
    const isEmptyRef = useRef(true);
    const isEmojiPickerOpenRef = useRef(false);
    const $ = useOverrides(overrides);
    const [isCollapsed, onCollapsedChange] = useControllableState(
      controlledCollapsed === void 0 && defaultCollapsed === void 0 ? false : controlledCollapsed,
      controlledOnCollapsedChange,
      defaultCollapsed
    );
    const setEmptyRef = useCallback((isEmpty) => {
      isEmptyRef.current = isEmpty;
    }, []);
    const setEmojiPickerOpenRef = useCallback((isEmojiPickerOpen) => {
      isEmojiPickerOpenRef.current = isEmojiPickerOpen;
    }, []);
    const handleFocus = useCallback(
      (event) => {
        onFocus?.(event);
        if (event.isDefaultPrevented()) {
          return;
        }
        if (isEmptyRef.current) {
          onCollapsedChange?.(false);
        }
      },
      [onCollapsedChange, onFocus]
    );
    const handleBlur = useCallback(
      (event) => {
        onBlur?.(event);
        if (event.isDefaultPrevented()) {
          return;
        }
        const isOutside = !event.currentTarget.contains(
          event.relatedTarget ?? document.activeElement
        );
        if (isOutside && isEmptyRef.current && !isEmojiPickerOpenRef.current) {
          onCollapsedChange?.(true);
        }
      },
      [onBlur, onCollapsedChange]
    );
    const handleEditorClick = useCallback(
      (event) => {
        event.stopPropagation();
        if (isEmptyRef.current) {
          onCollapsedChange?.(false);
        }
      },
      [onCollapsedChange]
    );
    const handleCommentSubmit = useCallback(
      (comment, event) => {
        onComposerSubmit?.(comment, event);
        if (event.isDefaultPrevented()) {
          return;
        }
        if (commentId && threadId) {
          editComment({
            commentId,
            threadId,
            body: comment.body,
            attachments: comment.attachments
          });
        } else if (threadId) {
          createComment({
            threadId,
            body: comment.body,
            attachments: comment.attachments
          });
        } else {
          createThread({
            body: comment.body,
            metadata: metadata ?? {},
            attachments: comment.attachments
          });
        }
      },
      [
        commentId,
        createComment,
        createThread,
        editComment,
        metadata,
        onComposerSubmit,
        threadId
      ]
    );
    return /* @__PURE__ */ React__default.createElement(TooltipProvider, null, /* @__PURE__ */ React__default.createElement(ComposerForm, {
      onComposerSubmit: handleCommentSubmit,
      className: classNames(
        "lb-root lb-composer lb-composer-form",
        className
      ),
      dir: $.dir,
      ...props,
      ref: forwardedRef,
      "data-collapsed": isCollapsed ? "" : void 0,
      onFocus: handleFocus,
      onBlur: handleBlur,
      disabled,
      defaultAttachments,
      pasteFilesAsAttachments: showAttachments
    }, /* @__PURE__ */ React__default.createElement(ComposerEditorContainer, {
      defaultValue,
      actions,
      overrides,
      isCollapsed,
      showAttachments,
      showAttribution,
      hasResolveMentionSuggestions,
      onEmptyChange: setEmptyRef,
      onEmojiPickerOpenChange: setEmojiPickerOpenRef,
      onEditorClick: handleEditorClick,
      autoFocus,
      disabled
    })));
  }
);

export { Composer };
//# sourceMappingURL=Composer.mjs.map
